/**
 * CreatePurchaseOrderModal - Modal for creating new purchase orders
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { purchaseOrdersAPI, vendorsAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Loader2, Search } from 'lucide-react';

const CreatePurchaseOrderModal = ({ open, onOpenChange, onSuccess, prefillVendor }) => {
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_name: '',
    description: '',
    notes: '',
  });
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Fetch vendors for dropdown
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await vendorsAPI.getVendors({ page_size: 100, is_active: true });
        setVendors(res.vendors || []);
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
      }
    };
    if (open) fetchVendors();
  }, [open]);

  // Search vendors
  useEffect(() => {
    if (vendorSearch.length >= 2) {
      const results = vendors.filter(v => 
        v.name.toLowerCase().includes(vendorSearch.toLowerCase())
      );
      setSearchResults(results.slice(0, 10));
    } else {
      setSearchResults([]);
    }
  }, [vendorSearch, vendors]);

  // Reset form or prefill vendor when modal opens/closes
  useEffect(() => {
    if (open) {
      // If prefillVendor provided, use it
      if (prefillVendor?.id) {
        setFormData({
          vendor_id: prefillVendor.id,
          vendor_name: prefillVendor.name || '',
          description: '',
          notes: '',
        });
      }
    } else {
      // Reset form when closing
      setFormData({
        vendor_id: '',
        vendor_name: '',
        description: '',
        notes: '',
      });
      setVendorSearch('');
      setSearchResults([]);
    }
  }, [open, prefillVendor]);

  const handleSelectVendor = (vendor) => {
    setFormData(prev => ({ ...prev, vendor_id: vendor.id, vendor_name: vendor.name }));
    setVendorSearch('');
    setSearchResults([]);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }

    setSubmitting(true);
    try {
      const result = await purchaseOrdersAPI.createPurchaseOrder({
        vendor_id: formData.vendor_id,
        description: formData.description || null,
        notes: formData.notes || null,
        items: [], // Items can be added later
      });
      const po = result.purchase_order || result;
      toast.success(`Purchase Order ${po.po_number || po.id} created`);
      onOpenChange(false);
      onSuccess?.(po.id);
    } catch (err) {
      toast.error(err.message || 'Failed to create purchase order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="create-po-modal">
        <DialogHeader>
          <DialogTitle>Create New Purchase Order</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Vendor *</Label>
            {formData.vendor_id ? (
              <div className="flex items-center justify-between p-3 bg-ivory-50 border rounded-lg">
                <span className="font-medium">{formData.vendor_name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, vendor_id: '', vendor_name: '' }))}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                    placeholder="Search vendors..."
                    className="pl-10"
                    data-testid="vendor-search-input"
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {searchResults.map(v => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => handleSelectVendor(v)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                      >
                        {v.name}
                      </button>
                    ))}
                  </div>
                )}
                {vendors.length > 0 && !vendorSearch && (
                  <select
                    value=""
                    onChange={(e) => {
                      const v = vendors.find(vendor => vendor.id === e.target.value);
                      if (v) handleSelectVendor(v);
                    }}
                    className="w-full h-10 px-3 border rounded-md bg-white text-sm"
                  >
                    <option value="">Or select from list...</option>
                    {vendors.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of this PO"
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          <p className="text-xs text-gray-500">
            Line items can be added after creation.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !formData.vendor_id}>
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              ) : (
                'Create PO'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePurchaseOrderModal;
